export interface Topic {
  id: string;
  title: string;
  category: string;
  excerpt: string;
  cover: string;
  author: string;
  date: string;
  readCount: number;
  commentCount: number;
  hot: number;
  heat: number;
  tags: string[];
  content: string[];
}

export interface CommentItem {
  id: string;
  topicId: string;
  nick: string;
  badge: string;
  text: string;
  time: string;
  likes: number;
}

export interface LotteryItem {
  id: string;
  title: string;
  prize: string;
  desc: string;
  participants: number;
  endTime: string;
  steps: string[];
  highlight: string;
}

export interface PollOption {
  id: string;
  label: string;
  votes: number;
}

export interface FaqItem {
  id: string;
  q: string;
  a: string;
}

export const brand = {
  name: "瓜田福利社",
  slogan: "边吃瓜，边拿福利",
  intro:
    "瓜田福利社是一个为热爱热闹的年轻人准备的内容互动社区。我们把每天值得聊的娱乐话题聚在一起，再配上转发抽奖、评论区打卡、粉丝投票这些能真正玩起来的玩法，让「吃瓜」不只停留在围观。",
  email: "hello@guatianfuli.com",
  serviceTime: "每天 09:00 - 23:00（评论区全天开放）",
  founded: "2023 年",
};

export const stats = [
  { id: "s1", label: "累计话题", value: "1,286", unit: "条" },
  { id: "s2", label: "每周互动", value: "42.6", unit: "万次" },
  { id: "s3", label: "打卡用户", value: "8.9", unit: "万" },
  { id: "s4", label: "已送出福利", value: "3,540", unit: "份" },
];

export const categories = [
  { id: "ent", name: "娱乐现场", icon: "ri-mic-line", desc: "演出、见面会与现场直击" },
  { id: "show", name: "影视综艺", icon: "ri-movie-line", desc: "剧集、综艺名场面复盘" },
  { id: "music", name: "音乐现场", icon: "ri-music-2-line", desc: "音乐节与livehouse观察" },
  { id: "star", name: "粉丝应援", icon: "ri-heart-3-line", desc: "应援创意与周边收藏" },
  { id: "web", name: "网红日常", icon: "ri-live-line", desc: "直播间与短视频名场面" },
  { id: "brand", name: "品牌联动", icon: "ri-gift-line", desc: "联名上新与福利情报" },
];

export const topics: Topic[] = [
  {
    id: "annual-best-moments",
    title: "年度最佳营业现场评选：这十个瞬间凭什么刷屏",
    category: "娱乐现场",
    excerpt:
      "我们翻遍了整整一年的现场返图与直拍，挑出十个被反复转发的瞬间。它们有的靠临场反应，有的靠一句没经过剪辑的真心话，看完你会明白为什么大家愿意一遍遍重播。",
    cover:
      "https://readdy.ai/api/search-image?query=warm%20coral%20and%20amber%20stage%20lights%20with%20soft%20bokeh%20and%20glowing%20particles%2C%20stylized%20editorial%20concert%20atmosphere%2C%20abstract%20cinematic%20backdrop%2C%20no%20text&width=800&height=600&seq=gt-topic-stage-lights&orientation=landscape",
    author: "瓜田编辑部",
    date: "2026-09-08",
    readCount: 18420,
    commentCount: 326,
    hot: 1,
    heat: 98,
    tags: ["年度盘点", "现场直拍", "名场面"],
    content: [
      "真正能被反复转发的现场瞬间，往往不是最贵的那一段舞美，而是某个来不及排练的反应。过去一年里，我们在评论区见到最多的追问就是：那段到底出自哪一场？于是我们把后台的素材重新过了一遍，按传播热度排出了这份榜单。",
      "排在第一位的是那次临时改词。演出进行到第二首歌，现场提词器突然黑屏，台上的歌手干脆把副歌换成了清唱，并临时加了一句对台下观众的致谢。这段没有任何机位特写的画面，因为一段观众手机直拍而扩散开来，三天内相关话题的讨论量翻了六倍。",
      "榜单里的第二个瞬间属于一次救场。乐手在换琴时琴弦断裂，鼓手顺势把节奏加快，把两分钟的空档变成了即兴solo，全场跟着打拍子。后台工作人员后来把这段音频单独剪出来，成为讨论度最高的现场片段之一。",
      "也有一些瞬间靠的是「反差」。一位以冷静著称的艺人在安可环节突然对着镜头比了个不太熟练的手势，被粉丝截成动图后广为传播。评论区最高赞的留言写着：这种不完美反而最像真人。",
      "我们把十个瞬间按类型分成了临场反应、情绪释放、互动救场三类，每一个都附上了大致的时间坐标。如果你也想补充自己心里的候选，欢迎在下方评论区打卡留言，我们会把高赞的提名整理进下一期更新。",
    ],
  },
  {
    id: "variety-show-golden-scenes",
    title: "综艺名场面复盘：笑到停不下来其实是有设计的",
    category: "影视综艺",
    excerpt:
      "很多人以为综艺的好笑全靠嘉宾即兴，其实真正的名场面背后有节奏、有铺垫、有剪辑。我们从三档热门节目里拆解出七个片段，看看节目组到底做了什么。",
    cover:
      "https://readdy.ai/api/search-image?query=colorful%20stylized%20television%20studio%20set%20with%20warm%20orange%20and%20mint%20green%20props%2C%20playful%20editorial%20illustration%2C%20soft%20studio%20lighting%2C%20abstract%20shapes%2C%20no%20text&width=800&height=600&seq=gt-topic-variety-set&orientation=landscape",
    author: "阿柚",
    date: "2026-09-06",
    readCount: 15230,
    commentCount: 251,
    hot: 3,
    heat: 93,
    tags: ["综艺", "名场面", "剪辑逻辑"],
    content: [
      "综艺的好笑程度，和嘉宾的知名度关系没有那么直接。我们在复盘时发现，被反复引用的片段几乎都符合一个共同规律：先给足信息，再打破预期，最后留出一秒钟的空白让观众自己笑出来。",
      "以第一个片段为例，节目先用两分钟建立了「这个人很怕辣」的设定，然后在游戏环节把辣度拉满。真正出圈的不是被辣到的那一刻，而是他一边喝水一边坚持完成挑战，还回头对队友说「这题我会了」的那句话。铺垫让反应变得可信，反转让它变得好笑。",
      "第二个片段则来自剪辑。素材里当事人只是安静地坐着，节目组把三段不同机位的反应镜头并排放在一起，配上一段突然抽掉背景音的静默处理，观众的笑点被精准地卡在静默结束的那一帧。",
      "也有反例。有几期节目塞了太多同类型的笑点，观众反而疲劳。这说明名场面不是靠数量堆出来的，而是需要留白。评论区里不少人的感受是：好的综艺会舍得让你喘口气。",
      "如果你正在追的节目里也有让你反复回看的名场面，可以在互动玩法页参与本周投票，选出你最想二刷的那一段。",
    ],
  },
  {
    id: "music-festival-backstage",
    title: "音乐节后台的十个真实瞬间，比舞台还好看",
    category: "音乐现场",
    excerpt:
      "舞台上是灯光与合唱，舞台下是到处找耳返、互相借发胶、蹲在地上改和弦。我们整理了十个后台瞬间，带你看音乐节不被直播拍到的那一面。",
    cover:
      "https://readdy.ai/api/search-image?query=stylized%20outdoor%20music%20festival%20backstage%20with%20warm%20sunset%20amber%20light%2C%20guitar%20cases%20and%20cables%20abstract%20composition%2C%20mint%20green%20accent%2C%20editorial%20illustration%2C%20no%20text&width=800&height=600&seq=gt-topic-festival-backstage&orientation=landscape",
    author: "小满在现场",
    date: "2026-09-04",
    readCount: 9870,
    commentCount: 148,
    hot: 6,
    heat: 84,
    tags: ["音乐节", "后台", "现场记录"],
    content: [
      "音乐节的后台永远比想象中拥挤。演出开始前两小时，走廊里能同时听到三首不同的歌，每个休息室都在用自己的方式热身，有人开嗓，有人只是戴着耳机来回踱步。",
      "最常出现的画面是找东西。耳返、拨片、备用的琴弦在几支乐队之间来回传递，一位乐手告诉我们，他在一个下午里借出过四样东西，也借到了三样。这种临时协作反而成了巡演里最踏实的部分。",
      "第二个真实瞬间发生在临上台前的十分钟。有支乐队临时决定把压轴曲目换成一首节奏更慢的歌，因为台下的天色刚好暗下来。这个决定没有写进任何流程表，却成了当天返场呼声最高的环节。",
      "后台也有安静的时刻。一位鼓手在演出结束后坐在地上，把湿透的手套慢慢脱下来，很久没有说话。他说每次演完都会有几分钟耳鸣，需要用这段时间重新听见自己。",
      "这些片段不会被直播镜头记录，但它们决定了台上那两小时的样子。想参与后台故事征集的话，可以在评论区留下你印象最深的一场演出。",
    ],
  },
  {
    id: "drama-set-daily",
    title: "影视剧组的日常：一张定妆照背后的三百次调整",
    category: "影视综艺",
    excerpt:
      "你在屏幕上看到的一张定妆照，可能要经历造型、打光、色调的反复修改。我们记录了剧组一天的流程，看看一张照片是怎么从草案走到发布的。",
    cover:
      "https://readdy.ai/api/search-image?query=warm%20toned%20film%20set%20with%20camera%20rig%20and%20costume%20rack%2C%20soft%20amber%20and%20coral%20lighting%2C%20stylized%20editorial%20illustration%2C%20calm%20atmosphere%2C%20no%20text&width=800&height=600&seq=gt-topic-film-set&orientation=landscape",
    author: "剧组小场记",
    date: "2026-09-02",
    readCount: 7640,
    commentCount: 96,
    hot: 8,
    heat: 78,
    tags: ["剧组", "幕后", "造型"],
    content: [
      "定妆照听起来是开拍前的小事，实际上它决定了观众对角色的第一印象。剧组通常会在正式开拍前预留三到五天专门做这件事，从服装到妆发，每一步都留有调整的余地。",
      "第一轮试装往往不会公开。造型团队会根据剧本里的描述先做几个方向的草案，演员穿上后在监视器前走一遍，导演组再决定往哪个方向细化。这个阶段最常见的结果是全部推翻重来。",
      "到了拍摄当天，真正花时间的是光。同一套造型在冷光和暖光下会呈现出完全不同的气质，摄影组会反复测试，直到肤色和服装的质感都能兼顾。有时一张照片要拍上几百张，最后选中的那张未必是最完美的，而是最像角色的那张。",
      "发布前的最后一关是色调。修图不是把人变得更精致，而是让画面统一在一个情绪里。一位修图师说，他们常做的是减法：把多余的细节压下去，让观众的眼睛落在该落的地方。",
      "所以当你下次看到一张特别贴脸的定妆照，背后大概率是三百次以上的微调。这也是我们在吃瓜之外，愿意聊一聊幕后流程的原因。",
    ],
  },
  {
    id: "fan-support-awards",
    title: "粉丝应援大赏：应援车、灯牌与手幅的进化史",
    category: "粉丝应援",
    excerpt:
      "从手写横幅到整街灯箱，应援这件事用了不到十年就完成了迭代。我们梳理了几种主流应援形式的变化，也聊了聊它们为什么越来越讲究。",
    cover:
      "https://readdy.ai/api/search-image?query=stylized%20night%20city%20street%20with%20warm%20coral%20and%20amber%20glowing%20light%20boxes%2C%20abstract%20fan%20support%20aesthetic%2C%20mint%20green%20accents%2C%20editorial%20poster%20illustration%2C%20no%20text&width=800&height=600&seq=gt-topic-fan-support&orientation=landscape",
    author: "应援观察员",
    date: "2026-08-30",
    readCount: 11250,
    commentCount: 187,
    hot: 5,
    heat: 87,
    tags: ["粉丝应援", "灯牌", "手幅"],
    content: [
      "最早期的应援很简单：一块手写的布，几个人在场地外举着。道具粗糙，但目标明确，就是让台上的人一眼看到自己在这里。这种朴素的需求，是所有应援形式的起点。",
      "随后出现的是统一化的手幅。为了让远处也能看清，应援组开始规定字号、配色和留白，甚至做出排版模板供不同场次复用。手幅从手作变成了设计，也从个人行为变成了组织行为。",
      "灯牌的普及改变了整个应援生态。它解决了夜晚和远距离的问题，也让应援呈现出可以被航拍的规模感。为了不干扰演出，后来的灯牌开始强调控光和亮度分级，这反而推动了技术的迭代。",
      "近两年最受关注的是应援车和公共空间投放。它不再只服务于现场，而是把应援变成了一次公开的城市露出。随之而来的是更严格的审核流程和预算管理，应援组的角色越来越像一个小型项目团队。",
      "不管你认同哪一种形式，能确定的是：应援正在从「用力」走向「用心」。评论区欢迎分享你见过最有创意的一次应援。",
    ],
  },
  {
    id: "livestream-highlights",
    title: "直播间名场面合集：那些让人反复回看的瞬间",
    category: "网红日常",
    excerpt:
      "直播间最大的魅力是没法重来。一次口误、一次突然的连线、一次没忍住的真情流露，都可能成为第二天被剪成切片的名场面。",
    cover:
      "https://readdy.ai/api/search-image?query=stylized%20warm%20lit%20streaming%20room%20with%20ring%20light%20and%20soft%20coral%20amber%20glow%2C%20abstract%20desk%20setup%2C%20mint%20green%20accent%20shapes%2C%20editorial%20illustration%2C%20no%20text&width=800&height=600&seq=gt-topic-livestream&orientation=landscape",
    author: "切片收集站",
    date: "2026-08-28",
    readCount: 13480,
    commentCount: 214,
    hot: 4,
    heat: 90,
    tags: ["直播", "切片", "名场面"],
    content: [
      "直播和录播最大的区别在于没有第二次机会。正因为如此，直播间里那些不够完美的瞬间才格外有生命力，观众也愿意为这种真实感停留。",
      "最常见的名场面是口误。主播在介绍产品时不小心说反了卖点，愣住两秒后自己笑出声，弹幕立刻开始刷同一句话。这段切片往往比正经的宣传片段传播得更广。",
      "另一种是突然的连线。某次随机连麦抽到一位正在加班的观众，两人聊起深夜通勤的日常，气氛从热闹慢慢安静下来。这段没有剧本的对话被剪出来后，评论区最常说的一句话是「没想到能在直播间被安慰到」。",
      "也有靠节奏翻盘的例子。一次直播因为设备故障卡顿，主播索性放下流程，和观众聊了半小时最近的烦恼。那场的观看时长反而比平时更高，说明大家留下来不只是为了买东西。",
      "如果你也有反复回看的直播间瞬间，欢迎在下方评论区打卡补充，我们会整理成下一期合集。",
    ],
  },
  {
    id: "brand-collab-guide",
    title: "品牌联名上新指南：这次到底值不值得排队",
    category: "品牌联动",
    excerpt:
      "联名年年有，排队值不值却是另一回事。我们从设计、实用性和价格三个角度，聊了聊怎么判断一次联名是不是真香。",
    cover:
      "https://readdy.ai/api/search-image?query=stylized%20warm%20product%20display%20with%20amber%20and%20coral%20packaging%20boxes%2C%20mint%20green%20accents%2C%20soft%20studio%20lighting%2C%20editorial%20flatlay%20illustration%2C%20no%20text&width=800&height=600&seq=gt-topic-brand-collab&orientation=landscape",
    author: "福利情报组",
    date: "2026-08-26",
    readCount: 8210,
    commentCount: 133,
    hot: 7,
    heat: 81,
    tags: ["联名", "上新", "值不值"],
    content: [
      "联名的本质是两种信任的叠加。你信任品牌，也信任那个被联名的角色或作品，于是愿意为限定两个字买单。但限定不等于值得，判断标准需要再具体一点。",
      "第一看设计有没有真的想清楚。好的联名会把形象语言融入产品本身，而不是简单贴一个logo。判断方法很简单：把图案去掉，这个产品还好看吗？如果去掉之后毫无吸引力，那它更像是一次快闪。",
      "第二看实用性。我们会统计每期联名里真正会被日常使用的单品比例，比例高的系列，复购和二手保值通常也更好。相反，那些买回去只能摆着的东西，热度退得最快。",
      "第三看价格是否合理。限定会带来溢价，但溢价应该对应设计、材质或工艺的实际提升。如果只是换了个包装颜色，价格却翻了一倍，那就要谨慎了。",
      "我们会在互动玩法页持续更新联名福利情报，也会不定期放出体验名额。参与前记得先看清规则，理性排队。",
    ],
  },
  {
    id: "fan-collection-cabinet",
    title: "追星女孩的收藏柜：小卡、周边与门票的整理术",
    category: "粉丝应援",
    excerpt:
      "东西越收越多，柜子却越来越乱。我们采访了几位收藏超过三年的同好，总结出一套既不伤卡又实用的整理方法。",
    cover:
      "https://readdy.ai/api/search-image?query=stylized%20warm%20shelf%20with%20neatly%20arranged%20collectible%20cards%20and%20small%20boxes%2C%20amber%20and%20coral%20lighting%2C%20mint%20green%20accents%2C%20editorial%20interior%20illustration%2C%20no%20text&width=800&height=600&seq=gt-topic-collection-cabinet&orientation=landscape",
    author: "收纳小林",
    date: "2026-08-24",
    readCount: 6890,
    commentCount: 88,
    hot: 9,
    heat: 75,
    tags: ["收藏", "整理", "小卡"],
    content: [
      "收藏的第一原则是分类，而不是收纳。先按类别把手幅、小卡、门票、周边分开，再决定用什么样的容器。很多人整理失败，是因为一开始就急着买盒子，结果尺寸全都不合适。",
      "小卡的储存最怕两件事：受潮和挤压。推荐用硬质卡套配合防潮盒，竖直摆放而不是平叠。竖直摆放的好处是拿取时不会摩擦到相邻的卡面，长期来看更安全。",
      "门票和纸质周边建议扫描一份电子版。原件放进无酸素材的文件夹，电子版用于随时翻看和分享。这样既能保护原件，也不耽误日常回味。",
      "最后是留白的部分。不要试图把每一格都塞满，留出一点空间，未来才有地方放新的东西。一位受访者说，她特意空了一整层，用来放「还没遇到但一定会遇到的」。",
      "如果你也有自己的整理小技巧，欢迎在评论区参与打卡分享，我们会挑选实用的方法整理成图文指南。",
    ],
  },
  {
    id: "hot-search-decoded",
    title: "热搜是怎么来的：一条话题的完整传播路径",
    category: "娱乐现场",
    excerpt:
      "一个话题从出现到登上热搜，中间会经历什么？我们追踪了几个案例，试着还原一条内容被放大之前的真实轨迹。",
    cover:
      "https://readdy.ai/api/search-image?query=stylized%20abstract%20data%20flow%20with%20warm%20coral%20amber%20and%20mint%20green%20lines%2C%20glowing%20nodes%20and%20soft%20gradient%20background%2C%20editorial%20tech%20illustration%2C%20no%20text&width=800&height=600&seq=gt-topic-hot-search&orientation=landscape",
    author: "瓜田编辑部",
    date: "2026-08-22",
    readCount: 10540,
    commentCount: 162,
    hot: 2,
    heat: 95,
    tags: ["热搜", "传播", "话题分析"],
    content: [
      "热搜不是凭空出现的。绝大多数话题在被大范围看见之前，都已经在小圈子里完成了一轮自发传播。这一步往往发生在评论区、粉丝群和垂直社群里，规模不大，但情绪浓度很高。",
      "第二步是破圈。当话题具备了明确的画面感或一句好记的短语，它就会被路人接住。这时候推动传播的已经不只是最初的关注者，而是那些原本对此并不熟悉的人。",
      "第三步是平台聚合。当讨论量在短时间内集中上升，系统会把相关内容聚合到一起，形成可以点击的话题页。到这里，话题才算真正被放大。",
      "值得注意的是，热度会衰减。我们对三个案例做了时间线统计，多数话题的高峰只维持一到两天。这意味着持续产出比一次性爆发更重要，这也是我们坚持每天更新话题的原因。",
      "想了解我们怎么挑选每天更新的话题？可以在常见问题页看到完整的选题标准。",
    ],
  },
];

export const hotTopics = topics
  .slice()
  .sort((a, b) => a.hot - b.hot)
  .map((t) => ({ id: t.id, title: t.title, category: t.category, heat: t.heat, hot: t.hot }));

export const comments: CommentItem[] = [
  {
    id: "c1",
    topicId: "annual-best-moments",
    nick: "今天也在补番",
    badge: "连续打卡 12 天",
    text: "第四位那个救场真的有被震到，鼓手把两分钟的空档硬生生打成了全场大合唱，我循环了无数遍。",
    time: "2 小时前",
    likes: 268,
  },
  {
    id: "c2",
    topicId: "annual-best-moments",
    nick: "西瓜不加冰",
    badge: "活跃评论员",
    text: "清唱那段我也存了，没有提词器反而唱得更稳。有时候失误才是真的舞台呀。",
    time: "4 小时前",
    likes: 173,
  },
  {
    id: "c3",
    topicId: "annual-best-moments",
    nick: "路过的小汤圆",
    badge: "连续打卡 30 天",
    text: "提名一次安可环节的临场改词，虽然没进前十但我觉得也该有姓名。",
    time: "6 小时前",
    likes: 96,
  },
  {
    id: "c4",
    topicId: "annual-best-moments",
    nick: "半糖少冰",
    badge: "新晋瓜友",
    text: "看完马上把收藏夹翻出来重看了一遍，谢谢你替大家做整理。",
    time: "8 小时前",
    likes: 54,
  },
];

export const lotteries: LotteryItem[] = [
  {
    id: "lot-1",
    title: "本周应援周边礼盒",
    prize: "联名应援礼盒 ×1",
    desc: "参与转发本周末话题并完成评论区打卡，即可获得抽奖资格。",
    participants: 2864,
    endTime: "还剩 2 天 14 小时",
    steps: ["转发指定话题", "评论区打卡一次", "填写收货信息"],
    highlight: "最受欢迎",
  },
  {
    id: "lot-2",
    title: "音乐节周边体验装",
    prize: "音乐节周边体验装 ×20",
    desc: "为喜欢的现场投出一票，并在话题下留下你的观演记忆。",
    participants: 1732,
    endTime: "还剩 4 天 6 小时",
    steps: ["参与本周投票", "发布一条打卡评论", "等待开奖通知"],
    highlight: "中奖率最高",
  },
  {
    id: "lot-3",
    title: "品牌联名新品试用",
    prize: "联名新品体验装 ×50",
    desc: "关注品牌联动栏目，参与评论区问答即有机会获得新品试用。",
    participants: 3421,
    endTime: "还剩 6 天 3 小时",
    steps: ["阅读联名指南", "完成一道评论区问答", "确认联系方式"],
    highlight: "名额最多",
  },
];

export const poll = {
  id: "poll-week-36",
  question: "本周最想二刷的名场面是哪一段？",
  totalSeed: 5219,
  options: [
    { id: "p1", label: "临时改词后的全场合唱", votes: 2140 },
    { id: "p2", label: "鼓手即兴救场的两分钟", votes: 1486 },
    { id: "p3", label: "直播间随机连麦的深夜对话", votes: 1032 },
    { id: "p4", label: "安可环节不太熟练的手势", votes: 561 },
  ] as PollOption[],
};

export const checkin = {
  streakDays: 6,
  bestStreak: 21,
  totalUsers: 89420,
  todayUsers: 3260,
  rewards: [
    { id: "r1", days: 3, label: "3 天连签", reward: "评论区专属标识" },
    { id: "r2", days: 7, label: "7 天连签", reward: "抽奖资格 ×1" },
    { id: "r3", days: 21, label: "21 天连签", reward: "联名周边礼盒" },
  ],
};

export const faqs: FaqItem[] = [
  {
    id: "f1",
    q: "如何参与福利吃瓜转发抽奖？需要满足哪些条件？",
    a: "在互动玩法页找到正在进行中的抽奖卡片，点击「一键参与」后按提示完成转发话题、评论区打卡等指定动作即可。每个账号在单个活动内只计一次参与，重复参与不会提高中奖概率。",
  },
  {
    id: "f2",
    q: "评论区打卡每天几点刷新？",
    a: "打卡以自然日为周期，每天 00:00 刷新。只要在当天任意时间完成一次话题评论，就算作当日打卡成功。连续打卡的天数会在评论区昵称旁实时显示。",
  },
  {
    id: "f3",
    q: "粉丝投票每人可以投几次？",
    a: "每个投票话题每人限投一次，提交后不可修改，结果会即时更新。我们希望用一次投票的方式，让结果更接近真实的人数分布，而不是被反复刷票影响。",
  },
  {
    id: "f4",
    q: "抽奖结果在哪里公布？",
    a: "开奖后结果会同步显示在对应的抽奖卡片上，并通过站内通知提醒已参与者。请在参与时确认联系方式有效，以便我们及时联系中奖用户。",
  },
  {
    id: "f5",
    q: "我可以同时参加多个互动活动吗？",
    a: "可以。转发抽奖、评论区打卡和粉丝投票互不冲突，你可以同时参与全部进行中的活动。不过部分抽奖会限制单人重复中奖，具体以活动说明为准。",
  },
  {
    id: "f6",
    q: "参与互动需要注册账号吗？",
    a: "浏览话题不需要注册。目前打卡、投票等互动在浏览器本地即可体验；如果你希望这些记录在不同设备之间同步，可以联系我们开启账号体系，届时数据会保存到安全的服务端。",
  },
];

export const fallbackComments: CommentItem[] = [
  {
    id: "fb1",
    topicId: "any",
    nick: "路过的小汤圆",
    badge: "连续打卡 30 天",
    text: "来打个卡，这篇整理得挺清楚，先收藏慢慢看。评论区果然还是最有意思的地方。",
    time: "1 小时前",
    likes: 142,
  },
  {
    id: "fb2",
    topicId: "any",
    nick: "半糖少冰",
    badge: "新晋瓜友",
    text: "第一次在这里留言，感觉大家都挺友善的，没有上来就吵架的那种氛围。",
    time: "3 小时前",
    likes: 88,
  },
  {
    id: "fb3",
    topicId: "any",
    nick: "只吃瓜不发言",
    badge: "活跃评论员",
    text: "顺手投了票，一人一票这个设定挺好的，结果看起来更真实一点。",
    time: "5 小时前",
    likes: 61,
  },
  {
    id: "fb4",
    topicId: "any",
    nick: "今天也在补番",
    badge: "连续打卡 12 天",
    text: "今天的打卡完成，明天见。希望多发一点幕后类型的内容。",
    time: "昨天",
    likes: 37,
  },
];

export const testimonials = [
  {
    id: "t1",
    name: "夜猫子小陈",
    role: "连续打卡 46 天",
    text: "每天睡前先来打个卡已经变成习惯了，评论区比正片还精彩，经常在这里捡到没追过的现场。",
  },
  {
    id: "t2",
    name: "只吃瓜不发言",
    role: "资深围观群众",
    text: "最喜欢它的话题整理，不用自己在信息流里翻半天。投票也做得挺克制，一人一票，结果看着比较真实。",
  },
  {
    id: "t3",
    name: "应援组阿鑫",
    role: "应援策划",
    text: "转发抽奖的规则写得很清楚，不会为了数据让人反复操作。中过一次周边，流程也走得挺顺。",
  },
];